import React from 'react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';

interface SparklineChartProps {
  data: number[];
  color?: string;
  height?: number;
  width?: number;
}

const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  color = '#f97316', // Orange PMT3 theme color
  height = 40,
  width = 80,
}) => {
  // Check if all values are 0 or if data is empty
  const hasData = data && data.length > 0 && data.some(value => value > 0);

  // If no data, show dash
  if (!hasData) {
    return (
      <div 
        className="flex items-center justify-center text-gray-400 dark:text-gray-500"
        style={{ width, height }}
      >
        <span className="text-sm font-medium">-</span>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = data.map((value, index) => ({
    week: index + 1,
    value: value,
  }));

  // Calculate domain for better scaling to use full height
  const minValue = Math.min(...data);
  const maxValue = Math.max(...data);
  const range = maxValue - minValue;
  const padding = range > 0 ? range * 0.05 : 1; // 5% padding or minimum of 1
  
  const yAxisDomain = [
    Math.max(0, minValue - padding), // Don't go below 0
    maxValue + padding
  ];

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <YAxis
            hide
            domain={yAxisDomain}
            type="number"
          />
          <Line
            type="linear"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default React.memo(SparklineChart);